import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDateTime } from "../../globalFunctions/DateTimeDisplayCleaner";
import { XMarkIcon } from "@heroicons/react/24/solid";
import QrScanner from "../../components/controls/QrScanner";

export default function TicketCheckinPage() {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [selectedEventId, setSelectedEventId] = useState("");
  const [events, setEvents] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchEmail, setSearchEmail] = useState("");
  const [emailTickets, setEmailTickets] = useState([]);
  const [emailError, setEmailError] = useState("");
  const [scannerVisible, setScannerVisible] = useState(false);

  useEffect(() => {
    const fetchEvents = async () => {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Event?showAll=false`
      );
      const data = await response.json();
      data.forEach((event) => (event.name = event.name.toUpperCase()));
      setEvents(data);
    };

    fetchEvents();
  }, []);

  const handleCheck = async () => {
    if (!code || !selectedEventId) return;

    setLoading(true);
    setResult(null);
    setError("");
    setEmailTickets([]);
    setSearchEmail("");

    // ➡️ Ajout logique pour préfixer AR3- si absent
    const formattedCode = code.startsWith("AR3-") ? code : `AR3-${code}`;

    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_API_BASE_URL
        }/api/Event/ticket/validate?code=${encodeURIComponent(formattedCode)}`
      );

      if (!response.ok) {
        setError("Billet non valide ou non payé.");
        setLoading(false);
        return;
      }

      const data = await response.json();

      if (data.eventId !== selectedEventId) {
        setError("Ce billet n'appartient pas à l'événement sélectionné.");
        setLoading(false);
        return;
      }

      if (data.isCheckedIn) {
        setError("Ce billet a déjà été validé.");
        setLoading(false);
        return;
      }

      setResult(data);
    } catch {
      setError("Erreur lors de la validation.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchByEmail = async () => {
    if (!searchEmail) return;

    setEmailTickets([]);
    setEmailError("");
    setError("");
    setCode("");
    setResult(null);

    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_API_BASE_URL
        }/api/Event/tickets/by-email?email=${encodeURIComponent(searchEmail)}`
      );

      if (!response.ok) {
        setEmailError("Aucun billet trouvé pour cet email.");
        return;
      }

      const data = await response.json();
      setEmailTickets(data);
    } catch {
      setEmailError("Erreur lors de la recherche.");
    }
  };

  const handleValidateTicket = async (ticketId) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/Event/ticket/validate/checkin`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ticketId }),
      }
    );

    if (response.ok) {
      setEmailTickets((prev) =>
        prev.map((t) => (t.id === ticketId ? { ...t, isCheckedIn: true } : t))
      );
    }
  };

  const handleCheckWithCode = async (scanCode) => {
    setLoading(true);
    setResult(null);
    setError("");
    setEmailTickets([]);
    setSearchEmail("");

    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_API_BASE_URL
        }/api/Event/ticket/validate?code=${encodeURIComponent(scanCode)}`
      );

      if (!response.ok) {
        setError("Billet non valide ou non payé.");
        setLoading(false);
        return;
      }

      const data = await response.json();

      if (data.eventId !== selectedEventId) {
        setError("Ce billet n'appartient pas à l'événement sélectionné.");
        setLoading(false);
        return;
      }

      if (data.isCheckedIn) {
        setError("Ce billet a déjà été validé.");
        setLoading(false);
        return;
      }

      setResult(data);
    } catch {
      setError("Erreur lors de la validation.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto p-6 bg-white rounded-xl shadow-md mt-10 text-primary">
      <h1 className="text-2xl font-bold mb-4">{"Validation des billets"}</h1>

      {/* 1 - Sélection événement */}
      <div className="mb-4">
        <h2 className="block font-semibold mb-1">Sélectionnez l'événement</h2>
        <select
          className="select select-bordered w-full"
          value={selectedEventId}
          onChange={(e) => setSelectedEventId(e.target.value)}
        >
          <option value="">
            -- {t("admin.chooseEvent") || "Choisir un événement"} --
          </option>
          {events.map((e) => (
            <option key={e.id} value={e.id}>
              {e.name} ({formatDateTime(e.eventDate)})
            </option>
          ))}
        </select>
      </div>
      <button
        className="btn bg-secondary text-white rounded-full mt-2 mb-4"
        onClick={() => setScannerVisible((prev) => !prev)}
      >
        {scannerVisible ? "Cacher le scanner" : "Afficher le scanner"}
      </button>
      {scannerVisible && (
        <div className="mb-6">
          <h2 className="block font-semibold mb-1">Scanner un billet QR</h2>
          <QrScanner
            onScan={(data) => {
              if (!data) return;

              const formattedCode = data.startsWith("AR3-")
                ? data
                : `AR3-${data}`;
              if (formattedCode === code) return;

              setCode(formattedCode);
              handleCheckWithCode(formattedCode);
            }}
          />
        </div>
      )}

      {/* 2 - Recherche par # billet */}
      <h2 className="block font-semibold mb-1">Recherche par # billet</h2>
      <div className="flex gap-2 mb-4">
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Entrer le code du billet"
          className="input input-bordered flex-1"
          disabled={loading || !selectedEventId}
        />
        <button
          onClick={handleCheck}
          className="btn bg-primary text-white rounded-full max-w-[150px]"
          disabled={loading || !selectedEventId}
        >
          {loading ? t("loading") : "Rechercher" || "Valider"}
        </button>
      </div>

      {/* 3 - Recherche par adresse courriel */}
      <h2 className="block font-semibold mb-1">
        Recherche par adresse courriel
      </h2>
      <div className="flex gap-2 mb-2">
        <input
          type="email"
          value={searchEmail}
          onChange={(e) => setSearchEmail(e.target.value)}
          placeholder="Entrer l'adresse courriel"
          className="input input-bordered flex-1"
          disabled={loading || !selectedEventId}
        />
        <button
          onClick={handleSearchByEmail}
          className="btn bg-secondary rounded-full text-white rounded-full max-w-[150px]"
          disabled={loading || !selectedEventId}
        >
          Rechercher
        </button>
      </div>

      {/* 4 - Infos billet par code */}
      {result && (
        <div className="mt-4 p-4 border rounded bg-gray-50 space-y-2">
          <p className="text-green-600 font-semibold">
            ✅ Billet valide et payé
          </p>
          <div className="text-sm text-gray-700 space-y-1">
            <p>
              <strong>Événement :</strong> {result.eventName}
            </p>
            <p>
              <strong>Date :</strong> {formatDateTime(result.eventDate)}
            </p>
            <p>
              <strong>Acheteur :</strong> {result.buyerEmail}
            </p>
            <p>
              <strong>Acheté le :</strong> {formatDateTime(result.purchaseDate)}
            </p>
            {
              <button
                onClick={async () => {
                  await handleValidateTicket(ticket.id);
                }}
                className="btn bg-accent text-white rounded-full btn-sm "
              >
                Marquer Présent
              </button>
            }
          </div>
        </div>
      )}

      {error && (
        <p className="flex items-center gap-2 text-red-500 font-bold mt-4">
          <XMarkIcon className="w-8 h-8" />
          {error}
        </p>
      )}

      {/* 5 - Liste billets par email */}
      {emailError && <p className="text-red-500 text-sm mt-4">{emailError}</p>}

      {emailTickets.length > 0 && (
        <div className="space-y-3 mt-6">
          {emailTickets.map((ticket) => (
            <div
              key={ticket.id}
              className="p-3 border rounded bg-gray-50 flex flex-col md:flex-row md:items-center md:justify-between gap-2"
            >
              <div>
                <p className="text-sm">
                  <strong>Événement :</strong> {ticket.eventName} -{" "}
                  {formatDateTime(ticket.eventDate)}
                </p>
                <p className="text-sm">
                  <strong>Code :</strong>{" "}
                  <span className="font-mono">{ticket.code}</span>
                </p>
                <p className="text-sm">
                  <strong>Présence :</strong>{" "}
                  {ticket.isCheckedIn ? (
                    <span className="text-green-600">Déjà validé</span>
                  ) : (
                    <span className="text-yellow-600">Non validé</span>
                  )}
                </p>
              </div>
              {!ticket.isCheckedIn && (
                <button
                  onClick={async () => {
                    await handleValidateTicket(ticket.id);
                  }}
                  className="btn bg-accent text-white btn-sm md:ml-4 rounded-full"
                >
                  Marquer Présent
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
